import { useCallback, useRef, useState } from 'react';
import { useDispatch } from '../state/AppContext';
import { GO_TO_INPUT } from '../state/actions';
import UniverseCanvas from '../galaxy/UniverseCanvas';
import exampleUniverseData from '../galaxy/exampleUniverseData';
import Header from './Header';
import '../styles/universe-preview.css';
import '../styles/universe.css';

const PREVIEW_TOUR_STEPS = [
  {
    title: 'Your Music Universe',
    text: 'This is your universe — a living map of every artist you love and discover across all your galaxies. Each glowing node is an artist. Try pinching to zoom and dragging to pan around!',
    icon: 'universe',
  },
  {
    title: 'Clusters & Genres',
    text: 'Artists are grouped into colorful clusters based on genre similarity. The nebula clouds show how your taste naturally organizes itself. Zoom out to see the full picture.',
    icon: 'clusters',
  },
  {
    title: 'Discover New Music',
    text: 'The brighter, pulsing nodes are recommendations — artists you haven\'t heard yet that connect to your favorites. Tap any node to see who it is!',
    icon: 'discover',
  },
  {
    title: 'It Grows With You',
    text: 'Favorite artists from any galaxy and they\'ll appear here. Over time, your universe becomes a unique fingerprint of your musical taste.',
    icon: 'grow',
  },
];

function StepIcon({ icon }) {
  switch (icon) {
    case 'universe':
      return (
        <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="12" cy="12" r="3.5" fill="currentColor" />
          <ellipse cx="12" cy="12" rx="10" ry="3.5" stroke="currentColor" strokeWidth="0.8" opacity="0.5" />
        </svg>
      );
    case 'clusters':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
          <circle cx="8" cy="8" r="4" />
          <circle cx="16" cy="10" r="3" />
          <circle cx="12" cy="17" r="3.5" />
        </svg>
      );
    case 'discover':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
        </svg>
      );
    case 'grow':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
          <path d="M12 22V8" />
          <path d="M5 12l7-7 7 7" />
          <path d="M5 19h14" />
        </svg>
      );
    default:
      return null;
  }
}

function getBadgeLabel(node) {
  if (node.type === 'seed') {
    return node.source === 'favorite' ? 'Favorite' : 'Discovered';
  }
  if (node.isRecommendation) return 'Recommendation';
  return 'Artist';
}

function getBadgeClass(node) {
  if (node.type === 'seed' && node.source === 'favorite') return 'badge-favorite';
  if (node.type === 'seed') return 'badge-discovered';
  if (node.isRecommendation) return 'badge-rec';
  return 'badge-rec';
}

function UniversePreview() {
  const dispatch = useDispatch();
  const canvasRef = useRef(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showTour, setShowTour] = useState(true);

  const handleBack = useCallback(() => {
    dispatch({ type: GO_TO_INPUT });
  }, [dispatch]);

  const handleSelectNode = useCallback((node) => {
    if (node && !node._isClusterCenter && !node._isSuperclusterCenter) {
      setSelectedNode(node);
      setShowTour(false);
    } else {
      setSelectedNode(null);
    }
  }, []);

  const handleHoverNode = useCallback(() => {}, []);

  const handleCloseCard = useCallback(() => {
    setSelectedNode(null);
    setShowTour(true);
  }, []);

  const isLastStep = currentStep === PREVIEW_TOUR_STEPS.length - 1;
  const isFirstStep = currentStep === 0;
  const step = PREVIEW_TOUR_STEPS[currentStep];

  // Empty arrays — preview has no user data
  const emptyArr = [];

  return (
    <div className="universe-preview-view">
      {/* Header */}
      <div className="universe-header-overlay">
        <Header showBack onBack={handleBack} mapName="Universe Preview" />
      </div>

      {/* Full interactive canvas */}
      <UniverseCanvas
        ref={canvasRef}
        universeData={exampleUniverseData}
        favorites={emptyArr}
        discoveredArtists={emptyArr}
        dislikes={emptyArr}
        onSelectNode={handleSelectNode}
        onHoverNode={handleHoverNode}
      />

      {/* "Example" badge */}
      <div className="universe-preview-badge">
        <span className="universe-preview-badge-dot" />
        Example Universe — Explore with pinch & drag
      </div>

      {/* Zoom controls */}
      <div className="zoom-controls universe-preview-zoom">
        <button
          className="zoom-btn"
          onClick={() => canvasRef.current?.zoomBy(1.4)}
          title="Zoom in"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <line x1="12" y1="8" x2="12" y2="16" />
            <line x1="8" y1="12" x2="16" y2="12" />
          </svg>
        </button>
        <button
          className="zoom-btn"
          onClick={() => canvasRef.current?.zoomBy(0.7)}
          title="Zoom out"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <line x1="8" y1="12" x2="16" y2="12" />
          </svg>
        </button>
        <button
          className="zoom-btn"
          onClick={() => canvasRef.current?.resetView()}
          title="Reset view"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <path d="M15 3h6v6" />
            <path d="M9 21H3v-6" />
            <path d="M21 3l-7 7" />
            <path d="M3 21l7-7" />
          </svg>
        </button>
      </div>

      {/* Selected node info card */}
      {selectedNode && !selectedNode._isClusterCenter && (
        <div className="universe-preview-node-card">
          <button className="universe-card-close" onClick={handleCloseCard}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <div className="universe-card-header">
            <div
              className="universe-card-image universe-card-image-placeholder"
              style={{ width: 44, height: 44 }}
            >
              {selectedNode.name?.charAt(0) || '?'}
            </div>
            <div className="universe-card-info">
              <h4 className="universe-card-name">{selectedNode.name}</h4>
              <div className="universe-card-badges">
                <span className={`universe-card-badge ${getBadgeClass(selectedNode)}`}>
                  {getBadgeLabel(selectedNode)}
                </span>
              </div>
            </div>
          </div>
          <p className="universe-preview-node-hint">
            In your real universe, you can preview tracks, favorite artists, and open them in Spotify.
          </p>
        </div>
      )}

      {/* Tour card overlay — hidden when viewing a node */}
      {showTour && (
        <div className="universe-preview-tour-card">
          <div className="universe-preview-tour-icon">
            <StepIcon icon={step.icon} />
          </div>
          <div className="universe-preview-tour-header">
            <span className="universe-preview-tour-title">{step.title}</span>
            <span className="universe-preview-tour-counter">
              {currentStep + 1} / {PREVIEW_TOUR_STEPS.length}
            </span>
          </div>
          <p className="universe-preview-tour-text">{step.text}</p>
          <div className="universe-preview-tour-actions">
            <button className="universe-preview-tour-skip" onClick={handleBack}>
              Close
            </button>
            <div className="universe-preview-tour-nav">
              {!isFirstStep && (
                <button
                  className="universe-preview-tour-back"
                  onClick={() => setCurrentStep((s) => s - 1)}
                >
                  Back
                </button>
              )}
              <button
                className="universe-preview-tour-next"
                onClick={isLastStep ? handleBack : () => setCurrentStep((s) => s + 1)}
              >
                {isLastStep ? 'Get Started' : 'Next'}
              </button>
            </div>
          </div>
          {/* Step dots */}
          <div className="universe-preview-tour-dots">
            {PREVIEW_TOUR_STEPS.map((_, i) => (
              <span
                key={i}
                className={`universe-preview-tour-dot${i === currentStep ? ' active' : ''}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default UniversePreview;
